import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LockIcon, ShieldCheckIcon, WrenchIcon } from 'lucide-react';
import { AdminPageTitle, AdminTabs, AuditEntry, ConfirmationModal, Panel, PermissionGate, StatusBadge } from '../../components/admin/AdminPrimitives';
import { Button } from '../../components/ui/Button';
import { Switch } from '../../components/ui/Switch';
import { progressionRules, recordAudit, roleLabels, type AuditRecord } from '../../data/admin';
import { adminIntegrations, hasCapability, hydrationRuleVersions, integrationStatusLabels, prototypeAdminRole, setIntegrationStatus, type AdminIntegration, type AdminIntegrationStatus } from '../../data/adminOperations';
import type { IntegrationProviderId } from '../../types/integrations';
import { HYDRATION_ML_PER_KG, formatLiters, hydrationTargetMl } from '../../utils/hydration';
import { useLocale } from '../../contexts/LocaleContext';
const tabs = [{
  id: 'geral',
  label: 'Geral'
}, {
  id: 'marca',
  label: 'Marca'
}, {
  id: 'idiomas',
  label: 'Idiomas'
}, {
  id: 'notificacoes',
  label: 'Notificações'
}, {
  id: 'moderacao',
  label: 'Moderação'
}, {
  id: 'gamificacao',
  label: 'Gamificação'
}, {
  id: 'hidratacao',
  label: 'Hidratação'
}, {
  id: 'seguranca',
  label: 'Segurança'
}, {
  id: 'integracoes',
  label: 'Integrações'
}, {
  id: 'manutencao',
  label: 'Manutenção'
}];
function Row({
  label,
  hint,
  children
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return <li className="flex items-center gap-3 border-b border-sand-line py-2 last:border-b-0" data-id="element-1961" data-mp="2l1nhsvz46gsz">
      <span className="min-w-0 flex-1" data-id="element-1962" data-mp="279zbj9bjqmjx">
        <span className="block text-[13px] font-medium text-deep" data-id="element-1963" data-mp="2ba9d89sb2qsz">{label}</span>
        {hint && <span className="mt-[2px] block text-[11.5px] leading-snug text-muted" data-id="element-1964" data-mp="2b7wy04dxmujm">{hint}</span>}
      </span>
      <span className="shrink-0" data-id="element-1965" data-mp="1xculuzihs8yd">{children}</span>
    </li>;
}

/**
 * Changes an integration's administrative status only — never opens a real
 * external connection. Confirmation + audit with old/new state.
 * REQUIRES BACKEND: `integrations.manage` authorization and persistence.
 */
function IntegrationStatusManager({
  onChanged
}: {
  onChanged: () => void;
}) {
  const {
    tl
  } = useLocale();
  const [serviceId, setServiceId] = useState<IntegrationProviderId>(adminIntegrations[0].id);
  const service = adminIntegrations.find(item => item.id === serviceId)!;
  const [next, setNext] = useState<AdminIntegrationStatus>(service.status);
  const [confirming, setConfirming] = useState(false);
  const [lastAudit, setLastAudit] = useState<AuditRecord | null>(null);
  const name = (item: AdminIntegration) => item.label ? tl(item.label) : item.name;
  const statuses = Object.keys(integrationStatusLabels) as AdminIntegrationStatus[];
  return <div className="space-y-3" data-id="element-1966" data-mp="32it1xfwnb8fm">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-1967" data-mp="32j2vtekt9zql">
        <label className="block" data-id="element-1968" data-mp="2xif1aixdj0rm">
          <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1969" data-mp="2xhdhnq2al5ya">Serviço</span>
          <select value={serviceId} onChange={event => {
          const id = event.target.value as IntegrationProviderId;
          setServiceId(id);
          setNext(adminIntegrations.find(item => item.id === id)!.status);
        }} className="mt-1 h-10 w-full rounded-card border border-sand-line bg-white px-2 text-[12.5px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-1970" data-mp="2ngb7adsrwe65">
            {adminIntegrations.map(item => <option key={item.id} value={item.id} data-id="element-1971" data-mp="3b2rcp8pkn3c3">
                {name(item)}
              </option>)}
          </select>
        </label>
        <label className="block" data-id="element-1972" data-mp="3l28gfkl9j75f">
          <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1973" data-mp="3l1765ic2moor">Novo status</span>
          <select value={next} onChange={event => setNext(event.target.value as AdminIntegrationStatus)} className="mt-1 h-10 w-full rounded-card border border-sand-line bg-white px-2 text-[12.5px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-1974" data-mp="1lqfs0ccjja4b">
            {statuses.map(status => <option key={status} value={status} data-id="element-1975" data-mp="3c8m6yq5kj94k">
                {tl(integrationStatusLabels[status])}
              </option>)}
          </select>
        </label>
      </div>
      <p className="text-[12px] leading-relaxed text-muted" data-id="element-1976" data-mp="3l7zdb9yebhl2">
        Status atual: <span className="font-bold text-deep" data-id="element-1977" data-mp="2xieucr28skbs">{tl(integrationStatusLabels[service.status])}</span>.
        Alterar o status administrativo não conecta nenhuma conta nem inicia sincronização real.
      </p>
      <Button variant="outline" disabled={next === service.status} onClick={() => setConfirming(true)} data-id="element-1978" data-mp="3786pbme97fgs">
        Revisar alteração
      </Button>

      {lastAudit && <div className="rounded-card border border-sand-line bg-white px-3" role="status" data-id="element-1979" data-mp="2eqm4h5igzf8a">
          <p className="bee-overline pt-2 text-[8.5px] font-bold text-muted" data-id="element-1980" data-mp="29skioctnt1t4">Registrado na auditoria</p>
          <ul data-id="element-1981" data-mp="p9gq1bhtm85l">
            <AuditEntry admin={lastAudit.admin} role={tl(roleLabels[lastAudit.role])} action={tl(lastAudit.action)} entity={lastAudit.entity} timestamp={lastAudit.timestamp} context={tl(lastAudit.context)} before={lastAudit.before} after={lastAudit.after} sensitive data-id="element-1982" data-mp="p8xi8nlvx05a" />
          </ul>
        </div>}

      <ConfirmationModal open={confirming} destructive title="Alterar o status desta integração?" body={`${name(service)}: ${tl(integrationStatusLabels[service.status])} → ${tl(integrationStatusLabels[next])}. A mudança fica registrada na auditoria com o estado anterior e o novo.`} confirmLabel="Confirmar alteração" requireText="CONFIRMAR" onConfirm={() => {
      if (!hasCapability(prototypeAdminRole, 'integrations.manage')) return;
      const before = integrationStatusLabels[service.status];
      const after = integrationStatusLabels[next];
      setIntegrationStatus(service.id, next);
      setLastAudit(recordAudit({
        action: {
          'pt-BR': 'Alterou status de integração',
          'en-US': 'Changed integration status'
        },
        entity: `integration:${service.id}`,
        context: {
          'pt-BR': `${service.name} · status administrativo`,
          'en-US': `${service.name} · administrative status`
        },
        before: before['pt-BR'],
        after: after['pt-BR'],
        severity: 'sensitive'
      }));
      setConfirming(false);
      onChanged();
    }} onClose={() => setConfirming(false)} data-id="element-1983" data-mp="18vv52tec3qbk" />
    </div>;
}
export function AdminSettings() {
  const {
    tab: param
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const tab = tabs.some(item => item.id === param) ? param as string : 'geral';
  const [flags, setFlags] = useState<Record<string, boolean>>({
    signups: true,
    social: true,
    autoHide: true,
    holdSuspect: true,
    healthPriority: true,
    marketingAsHealth: false,
    ipAllow: false,
    force2fa: true,
    maintenance: false
  });
  const [confirm, setConfirm] = useState<string | null>(null);
  // Re-render the status table after a confirmed integration change.
  const [, setIntegrationsVersion] = useState(0);
  const toggle = (id: string) => setFlags(prev => ({
    ...prev,
    [id]: !prev[id]
  }));
  return <div className="space-y-4" data-id="element-1984" data-mp="3vvkcv1wawc8m">
      <AdminPageTitle title="Configurações da plataforma" subtitle="Parâmetros operacionais do BEELYV. A identidade visual já está aprovada e não é editável aqui." data-id="element-1985" data-mp="3vwlphasyt6sg" />

      <AdminTabs tabs={tabs} active={tab} onChange={next => navigate(`/admin/config/${next}`)} data-id="element-1986" data-mp="8r4jqt3q8n41" />

      {tab === 'geral' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-1987" data-mp="1k4yl03xvxpzp">
          <Panel title="Plataforma" data-id="element-1988" data-mp="1k3dsejv4sh4h">
            <ul data-id="element-1989" data-mp="266id42y1zx02">
              <Row label="Novos cadastros" hint="Permite criação de contas no aplicativo." data-id="element-1990" data-mp="265z5bf24aozr">
                <Switch checked={flags.signups} onChange={() => toggle('signups')} label="Novos cadastros" data-id="element-1991" data-mp="217xm8u4oypoa" />
              </Row>
              <Row label="Camada social" hint="Feed, Pulse e Explore disponíveis para a base." data-id="element-1992" data-mp="2fxd75thjk0w2">
                <Switch checked={flags.social} onChange={() => toggle('social')} label="Camada social" data-id="element-1993" data-mp="2b4k3za1q10m3" />
              </Row>
              <Row label="Versão do aplicativo" data-id="element-1994" data-mp="2ov5fgdoui89y">
                <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-1995" data-mp="2jyezdqd1jcoj">1.0.0</span>
              </Row>
            </ul>
          </Panel>
          <Panel title="Privacidade por padrão" data-id="element-1996" data-mp="2yuny8qt0ro0f">
            <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-muted" data-id="element-1997" data-mp="2twco0eytbbho">
              <ShieldCheckIcon size={15} className="mt-[1px] shrink-0 text-gold" aria-hidden="true" data-id="element-1998" data-mp="14h50pgyzo5f4" />
              Métricas corporais são privadas por padrão em toda a plataforma e só se tornam
              públicas com consentimento explícito do usuário. Nenhuma configuração aqui
              inverte esse comportamento.
            </p>
          </Panel>
        </div>}

      {tab === 'marca' && <Panel title="Identidade" hint="Somente leitura." data-id="element-1999" data-mp="31fvqw5rknzh6">
          <ul className="flex flex-wrap gap-3" data-id="element-2000" data-mp="31dhv1up2ij8k">
            {[['Deep Green', '#102A2A'], ['Honey Gold', '#C89432'], ['Sand', '#F5F1E6'], ['Soft Green', '#2E4D45'], ['Black', '#111111']].map(([name, hex]) => <li key={hex} className="w-[128px]" data-id="element-2001" data-mp="2whu2r6fah9yu">
                <span className="block h-16 w-full rounded-card border border-sand-line" style={{
            background: hex
          }} aria-hidden="true" data-id="element-2002" data-mp="2ihjdg7tmo3fs" />
                <span className="mt-1 block text-[12px] font-medium text-deep" data-id="element-2003" data-mp="gqeo5if63eym">{name}</span>
                <span className="bee-num block text-[11px] text-muted" data-id="element-2004" data-mp="lopxls1jvngb">{hex}</span>
              </li>)}
          </ul>
          <p className="mt-3 flex items-center gap-1 text-[11.5px] text-muted" data-id="element-2005" data-mp="zf0oi1uw3ahd">
            <LockIcon size={13} aria-hidden="true" data-id="element-2006" data-mp="148bvc2zpbr9a" />
            A identidade BEELYV está aprovada e travada. Alterações passam pelo time de
            design, não por esta tela.
          </p>
        </Panel>}

      {tab === 'idiomas' && <Panel title="Idiomas suportados" data-id="element-2007" data-mp="vcm2x9zu5wme">
          <ul data-id="element-2008" data-mp="1j4az51fs358m">
            <Row label="Português (Brasil)" hint="Idioma padrão da plataforma." data-id="element-2009" data-mp="1j5vwszxe34ap">
              <StatusBadge status="active" label="Ativo" data-id="element-2010" data-mp="1n6fr7vqkvtt5" />
            </Row>
            <Row label="English (US)" hint="Todo conteúdo exige as duas versões." data-id="element-2011" data-mp="196n4scm33zqz">
              <StatusBadge status="active" label="Ativo" data-id="element-2012" data-mp="1e5hn7dg53mak" />
            </Row>
          </ul>
        </Panel>}

      {tab === 'notificacoes' && <Panel title="Prioridade de comunicação" data-id="element-2013" data-mp="3tvugd91pk729">
          <ul data-id="element-2014" data-mp="2qb70cn6cjvx">
            <Row label="Lembretes de saúde têm prioridade" hint="Saúde sempre acima de avisos sociais e de campanha." data-id="element-2015" data-mp="2quborntdbs0">
              <Switch checked={flags.healthPriority} onChange={() => toggle('healthPriority')} label="Prioridade de saúde" data-id="element-2016" data-mp="3reajkzw2ou3e" />
            </Row>
            <Row label="Permitir campanha com formato de saúde" hint="Mantido desligado: marketing não pode se passar por alerta de saúde." data-id="element-2017" data-mp="45ds4wino34l">
              <Switch checked={flags.marketingAsHealth} onChange={() => setConfirm('marketingAsHealth')} label="Campanha como saúde" data-id="element-2018" data-mp="37oglk42rliqq" />
            </Row>
          </ul>
        </Panel>}

      {tab === 'moderacao' && <Panel title="Regras da fila" data-id="element-2019" data-mp="3cmi66tfu9q4b">
          <ul data-id="element-2020" data-mp="3clgtyln36xl0">
            <Row label="Ocultar automaticamente com 5+ denúncias" hint="O conteúdo sai do ar até a revisão humana." data-id="element-2021" data-mp="2oxfoxmp49zac">
              <Switch checked={flags.autoHide} onChange={() => toggle('autoHide')} label="Ocultar automaticamente" data-id="element-2022" data-mp="2jycjw294zt2d" />
            </Row>
            <Row label="Reter conteúdo de contas novas" hint="Primeiros 7 dias passam por revisão antes de aparecer no Explore." data-id="element-2023" data-mp="suaya99tzn1d">
              <Switch checked={flags.holdSuspect} onChange={() => toggle('holdSuspect')} label="Reter contas novas" data-id="element-2024" data-mp="xndsmjmfn0im" />
            </Row>
          </ul>
        </Panel>}

      {tab === 'gamificacao' && <Panel title="Regras vigentes" hint="Somente leitura nesta tela." data-id="element-2025" data-mp="4yyfy3qrwuag">
          <ul data-id="element-2026" data-mp="9vynkj39u53o">
            {progressionRules.map(rule => <Row key={rule.id} label={tl(rule.label)} data-id="element-2027" data-mp="eujn0gap8kr2">
                <span className="flex items-center gap-2" data-id="element-2028" data-mp="ex5u6etogy8r">
                  <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-2029" data-mp="ix67tx8yd5er">{rule.value}</span>
                  <LockIcon size={13} className="text-muted" aria-hidden="true" data-id="element-2030" data-mp="1q96mu4tnx6ts" />
                </span>
              </Row>)}
          </ul>
        </Panel>}

      {tab === 'seguranca' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-2031" data-mp="3et2ru89o6lht">
          <Panel title="Acesso administrativo" data-id="element-2032" data-mp="3arh9i7ed9f08">
            <ul data-id="element-2033" data-mp="3assj4nuhtgz9">
              <Row label="Exigir 2FA para toda a equipe" hint="Bloqueia o acesso sem segundo fator." data-id="element-2034" data-mp="3aud6ntie3znm">
                <Switch checked={flags.force2fa} onChange={() => toggle('force2fa')} label="Exigir 2FA" data-id="element-2035" data-mp="3127eojs51i8f" />
              </Row>
              <Row label="Restringir por IP" hint="Permite acesso apenas de faixas aprovadas." data-id="element-2036" data-mp="2w3meulgst0ki">
                <Switch checked={flags.ipAllow} onChange={() => toggle('ipAllow')} label="Restringir por IP" data-id="element-2037" data-mp="2r3i5rgi7z84b" />
              </Row>
              <Row label="Expiração de sessão" hint="Tempo de inatividade até encerrar a sessão." data-id="element-2038" data-mp="2n37qfd8jf5cs">
                <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-2039" data-mp="2i4mxzzzj1pyw">30 min</span>
              </Row>
            </ul>
          </Panel>
          <Panel title="Menor privilégio" data-id="element-2040" data-mp="l8p660nqt9y7">
            <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-2041" data-mp="gd07dzjjrx2y">
              O acesso a qualquer dado de saúde individual exige justificativa, é limitado ao
              papel que atende o usuário e gera evento de auditoria. A autorização é sempre
              validada no backend.
            </p>
          </Panel>
        </div>}

      {tab === 'hidratacao' && <div className="space-y-3" data-id="element-2042" data-mp="354c3lphrlkn5">
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-2043" data-mp="353ag2ljkb5oj">
            <Panel title="Regra ativa de hidratação" hint="Regra controlada de produto." data-id="element-2044" data-mp="2qfpewx68zgj9">
              <div className="flex items-baseline justify-between gap-3" data-id="element-2045" data-mp="2v77h1ok0bn5t">
                <p className="bee-num text-[26px] font-bold leading-none text-deep" data-id="element-2046" data-mp="2v8igjc6f68r4">
                  {HYDRATION_ML_PER_KG} ml/kg
                </p>
                <StatusBadge status="active" label="Ativa" data-id="element-2047" data-mp="2lhnwmegnbl8u" />
              </div>
              <p className="mt-2 text-[12.5px] text-muted" data-id="element-2048" data-mp="27gtl3myax45y">
                Meta diária sugerida = {HYDRATION_ML_PER_KG} ml × peso corporal em kg
              </p>
              <dl className="mt-3 space-y-2" data-id="element-2049" data-mp="2ci0svm0g23cm">
                {[['Exemplo', `70 kg × ${HYDRATION_ML_PER_KG} ml = ${formatLiters(hydrationTargetMl(70) ?? 0, 'pt-BR')} por dia`], ['Sem peso informado', 'Nenhuma meta é gerada; o usuário é convidado a adicionar peso.'], ['Peso em lbs', 'Convertido para kg antes do cálculo.'], ['Histórico', 'Dias anteriores mantêm a meta com que foram registrados.'], ['Daily Check', 'Hidratação vale 15 Bee; a base total segue 100 Bee.']].map(([label, value]) => <div key={label} className="flex items-baseline justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-2050" data-mp="2lj23vo89gnsb">
                    <dt className="shrink-0 text-[12px] text-muted" data-id="element-2051" data-mp="2ujgrwvcm0gzi">{label}</dt>
                    <dd className="text-right text-[12.5px] font-medium text-deep" data-id="element-2052" data-mp="2uhds5jlnn9hl">{value}</dd>
                  </div>)}
              </dl>
            </Panel>

            <Panel title="Alterar coeficiente" hint="Exige a permissão “Alterar regra de hidratação”." data-id="element-2053" data-mp="2xn65msw6243r">
              <PermissionGate allowed={hasCapability(prototypeAdminRole, 'rules.manageHydration')} area="regra de hidratação" data-id="element-2054" data-mp="32kphxn9b06t2">
                <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-2055" data-mp="3cb0zwdptogiy">
                  Uma alteração cria nova versão com data de vigência. A versão atual é
                  aposentada, não sobrescrita, e a mudança fica registrada na auditoria.
                </p>
                <Button variant="outline" className="mt-3" onClick={() => setConfirm('hydrationRule')} data-id="element-2056" data-mp="3q9h5u2fv2301">
                  <LockIcon size={15} data-id="element-2057" data-mp="3v9mxhim4rpi4" />
                  Propor nova versão
                </Button>
              </PermissionGate>
            </Panel>
          </div>

          <Panel title="Histórico de versões" hint="Somente leitura. Versões nunca são editadas depois de publicadas." data-id="element-2058" data-mp="1vyc6397waapz">
            <div className="overflow-x-auto" data-id="element-2059" data-mp="hzpr69owm17s">
              <table className="w-full min-w-[640px] border-collapse text-left" data-id="element-2060" data-mp="i1ivvjfyibd5">
                <thead data-id="element-2061" data-mp="d86ik5gq9qzp">
                  <tr className="border-b border-sand-line" data-id="element-2062" data-mp="d7y8smfwj1sq">
                    {['Versão', 'Coeficiente', 'Status', 'Vigência', 'Publicada por', 'Nota'].map(head => <th key={head} scope="col" className="py-2 pr-3" data-id="element-2063" data-mp="49ulplp9pwyt">
                        <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-2064" data-mp="10zbyslqkylaj">{head}</span>
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-2065" data-mp="rxm9pmk67yfx">
                  {hydrationRuleVersions.map(version => <tr key={version.version} className="border-b border-sand-line last:border-b-0" data-id="element-2066" data-mp="mz157axvyslo">
                      <td className="bee-num py-2 pr-3 text-[12.5px] font-bold text-deep" data-id="element-2067" data-mp="3co382c175rij">{version.version}</td>
                      <td className="bee-num py-2 pr-3 text-[12.5px] text-deep" data-id="element-2068" data-mp="37oqkfp2i4gew">{version.coefficientMlPerKg} ml/kg</td>
                      <td className="py-2 pr-3" data-id="element-2069" data-mp="1bl3oyqmhmhme">
                        <StatusBadge status={version.status} label={version.status === 'active' ? 'Ativa' : version.status === 'draft' ? 'Rascunho' : 'Aposentada'} data-id="element-2070" data-mp="1bohs412z6uuw" />
                      </td>
                      <td className="bee-num py-2 pr-3 text-[12px] text-muted" data-id="element-2071" data-mp="16nkbvu1j0aw1">{version.effectiveFrom}</td>
                      <td className="py-2 pr-3 text-[12px] text-deep" data-id="element-2072" data-mp="1udeocahxnxfr">{version.publishedBy}</td>
                      <td className="py-2 text-[12px] text-muted" data-id="element-2073" data-mp="1zb6cctfgizeo">{tl(version.note)}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>}

      {tab === 'integracoes' && <PermissionGate allowed={hasCapability(prototypeAdminRole, 'integrations.view')} area="integrações" data-id="element-2074" data-mp="2db8xcoj2a8y5">
          <div className="space-y-3" data-id="element-2075" data-mp="2i3iizsd58mum">
            <Panel title="Integrações" hint="Nenhuma integração está ativa. Nada aqui simula conexões reais." data-id="element-2076" data-mp="6myniruxyh06">
              <div className="overflow-x-auto" data-id="element-2077" data-mp="1nvkte2fomvd">
                <table className="w-full min-w-[900px] border-collapse text-left" data-id="element-2078" data-mp="gdjmwhifvpk5">
                  <thead data-id="element-2079" data-mp="bdykty7z3137">
                    <tr className="border-b border-sand-line" data-id="element-2080" data-mp="bdowef2kt1zm">
                      {['Serviço', 'Status', 'Contas conectadas', 'Última sincronização', 'Erros (24h)', 'Importação', 'Compartilhar posts'].map(head => <th key={head} scope="col" className="py-2 pr-3" data-id="element-2081" data-mp="3evyvjl4wxkki">
                          <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-2082" data-mp="3suouz7f7fwc1">{head}</span>
                        </th>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-2083" data-mp="18369hf3w1qbc">
                    {adminIntegrations.map(service => <tr key={service.id} className="border-b border-sand-line last:border-b-0" data-id="element-2084" data-mp="1435nn9egmcu9">
                        <th scope="row" className="py-2 pr-3 text-[12.5px] font-medium text-deep" data-id="element-2085" data-mp="sv7fulq3icce">
                          {service.label ? tl(service.label) : service.name}
                        </th>
                        <td className="py-2 pr-3" data-id="element-2086" data-mp="12u6nc0z4k1xt">
                          <StatusBadge status={service.status} label={tl(integrationStatusLabels[service.status])} data-id="element-2087" data-mp="12rtzx8alkzdd" />
                        </td>
                        <td className="bee-num py-2 pr-3 text-[12px] text-muted" data-id="element-2088" data-mp="17t9gan1tyhpf">
                          {service.connectedAccounts ?? '—'}
                        </td>
                        <td className="bee-num py-2 pr-3 text-[12px] text-muted" data-id="element-2089" data-mp="1lmir8s5ffba2">
                          {service.lastSync ?? '—'}
                        </td>
                        <td className="bee-num py-2 pr-3 text-[12px] text-muted" data-id="element-2090" data-mp="1vjdlgyuo2pij">
                          {service.syncErrors24h ?? '—'}
                        </td>
                        <td className="py-2 pr-3 text-[12px] text-muted" data-id="element-2091" data-mp="2a4rgszrk9sxa">
                          {service.importStatus ? tl(service.importStatus) : '—'}
                        </td>
                        <td className="py-2 text-[12px] text-muted" data-id="element-2092" data-mp="2073hdwj0fx8s">
                          {service.crossPost === 'supported' ? 'Suportado' : service.crossPost === 'planned' ? 'Previsto' : 'Não suportado'}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-2093" data-mp="1higdhhckldws">
                Campos operacionais aparecem como “—” até a integração existir. Status possíveis:
                Em breve, Desativada, Em teste e Ativa.
              </p>
            </Panel>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-2094" data-mp="1cghd8gc4mx42">
              <Panel title="Gerenciar status" hint="Exige a permissão “Gerenciar integrações”." data-id="element-2095" data-mp="tcnnwuaiwrfj">
                <PermissionGate allowed={hasCapability(prototypeAdminRole, 'integrations.manage')} area="gestão de integrações" data-id="element-2096" data-mp="omgywtc3n4wr">
                  <IntegrationStatusManager onChanged={() => setIntegrationsVersion(v => v + 1)} data-id="element-2097" data-mp="jkrq7kccn3bu" />
                </PermissionGate>
              </Panel>
              <Panel title="Privacidade" data-id="element-2098" data-mp="1cagqtwl5d1cx">
                <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-muted" data-id="element-2099" data-mp="1c9fdflgns2s1">
                  <ShieldCheckIcon size={15} className="mt-[1px] shrink-0 text-gold" aria-hidden="true" data-id="element-2100" data-mp="17audln5bjl44" />
                  A sincronização só ocorre após autorização explícita de cada usuário. Dados
                  importados seguem as configurações de privacidade dele e nunca ficam públicos
                  automaticamente. Compartilhamento externo é sempre opcional, post a post.
                </p>
              </Panel>
            </div>
          </div>
        </PermissionGate>}

      {tab === 'manutencao' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-2101" data-mp="22l903nsdrxzs">
          <Panel title="Modo manutenção" data-id="element-2102" data-mp="22jpgpobfh6ts">
            <ul data-id="element-2103" data-mp="27klls57imz0s">
              <Row label="Ativar modo manutenção" hint="O aplicativo passa a exibir o estado de manutenção para toda a base." data-id="element-2104" data-mp="27k3iiomfrkjd">
                <Switch checked={flags.maintenance} onChange={() => setConfirm('maintenance')} label="Modo manutenção" data-id="element-2105" data-mp="2hb7zftvo9fgu" />
              </Row>
              <Row label="Janela programada" hint="Aviso enviado 48h antes." data-id="element-2106" data-mp="2qb39fvdhr4fa">
                <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-2107" data-mp="2va7oh2n3jwdj">27/09 · 03:00</span>
              </Row>
            </ul>
          </Panel>

          <Panel title="Prévia do estado de manutenção" data-id="element-2108" data-mp="vywssh1tvvfl">
            <div className="flex flex-col items-center rounded-card border border-deep bg-deep px-4 py-6 text-center" data-id="element-2109" data-mp="10vwyuryogy6p">
              <span className="grid h-12 w-12 place-items-center rounded-pill border border-gold/45 bg-deeper text-gold" data-id="element-2110" data-mp="2rch6jlkdsef4">
                <WrenchIcon size={20} data-id="element-2111" data-mp="2me7ehjtydtxq" />
              </span>
              <p className="bee-overline mt-2 text-[8.5px] font-bold text-gold" data-id="element-2112" data-mp="30ei7otiqjkm2">Manutenção</p>
              <p className="mt-1 text-[15px] font-bold text-sand" data-id="element-2113" data-mp="2wc3kjx819yoz">Estamos em manutenção.</p>
              <p className="mt-1 max-w-[34ch] text-[12px] leading-relaxed text-ondark" data-id="element-2114" data-mp="28mj2dgjpf5gr">
                Uma melhoria está sendo aplicada. Voltamos em alguns minutos.
              </p>
            </div>
          </Panel>
        </div>}

      <ConfirmationModal open={confirm !== null} destructive title={confirm === 'maintenance' ? 'Ativar o modo manutenção?' : confirm === 'hydrationRule' ? 'Propor nova versão da regra de hidratação?' : 'Permitir campanha com formato de saúde?'} body={confirm === 'maintenance' ? 'Toda a base perde o acesso ao aplicativo até a desativação. A ação é registrada na auditoria com o seu nome e horário.' : confirm === 'hydrationRule' ? 'Uma nova versão muda a meta sugerida de todos os usuários a partir da data de vigência. Dias anteriores não são recalculados. A ação é registrada na auditoria.' : 'Isso permitiria que comunicações de marketing usassem o formato reservado a alertas de saúde. Não é recomendado e fica registrado na auditoria.'} confirmLabel="Confirmar" requireText="CONFIRMAR" onConfirm={() => {
      if (confirm && confirm !== 'hydrationRule') toggle(confirm);
      if (confirm === 'hydrationRule') {
        recordAudit({
          action: {
            'pt-BR': 'Propôs nova versão da regra de hidratação',
            'en-US': 'Proposed new hydration rule version'
          },
          entity: 'rule:hydration',
          context: {
            'pt-BR': 'Regra controlada de produto',
            'en-US': 'Controlled product rule'
          },
          before: `${HYDRATION_ML_PER_KG} ml/kg`,
          after: 'proposta pendente',
          severity: 'sensitive'
        });
      } else if (confirm) {
        recordAudit({
          action: confirm === 'maintenance' ? {
            'pt-BR': 'Alterou modo manutenção',
            'en-US': 'Changed maintenance mode'
          } : {
            'pt-BR': 'Alterou formato de campanha de saúde',
            'en-US': 'Changed health campaign format'
          },
          entity: 'platform',
          context: {
            'pt-BR': 'Configurações da plataforma',
            'en-US': 'Platform settings'
          },
          severity: 'sensitive'
        });
      }
      setConfirm(null);
    }} onClose={() => setConfirm(null)} data-id="element-2115" data-mp="2hgxgvgweubyt" />
    </div>;
}